import { AlertTriangle } from 'lucide-react';
import { Button } from './Button.jsx';

/**
 * FE-9: every asynchronous view handles error explicitly, with a retry.
 * FE-12: the message shown is the one the API returned, including field detail —
 * never a generic "something went wrong" when the server said something specific.
 */
export function ErrorNotice({ error, onRetry, title = 'Something went wrong', className = '' }) {
  const message = error?.message ?? String(error ?? 'Unknown error');
  const issues = error?.details?.issues ?? [];

  return (
    <div role="alert" className={`rounded-md border border-red-200 bg-red-50 px-4 py-3.5 ${className}`}>
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-red-700">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-red-700/90">{message}</p>

          {issues.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-red-700/80">
              {issues.map((issue, i) => (
                <li key={`${issue.field}-${i}`}>
                  <span className="font-medium">{issue.field}</span>: {issue.message}
                </li>
              ))}
            </ul>
          )}

          {(onRetry || error?.requestId) && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {onRetry && <Button size="sm" variant="secondary" onClick={onRetry}>Try again</Button>}
              {/* NF-1: the request id is what support needs to find this in the logs. */}
              {error?.requestId && (
                <span className="font-mono text-[11px] text-red-700/70">Request {error.requestId}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorNotice;
